import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import useSEO from '../hooks/useSEO';

const { FiPlus, FiMinus, FiMessageCircle, FiPhone, FiCheckCircle, FiBookOpen, FiShield, FiTruck, FiMapPin } = FiIcons;

const FAQ = () => {
  const [openCategory, setOpenCategory] = useState(0);
  const [openQuestion, setOpenQuestion] = useState(`${0}-${0}`);

  // Dynamic SEO for Google and AI Search
  useSEO({
    title: 'CARB Compliance Q&A | Mobile Emissions Testing FAQ Ontario CA',
    description: 'Expert answers to CARB Clean Truck Check rules, mobile diesel emissions testing, DOT inspections, and DPF force regens in Ontario, Fontana, and the Inland Empire.',
    canonicalRoute: '/faq'
  });

  // Categorized FAQs heavily optimized for Local SEO and AI Entity Recognition
  const faqData = [
    {
      category: "CARB Compliance & HD I/M Rules",
      icon: FiShield,
      questions: [
        {
          q: "What is the CARB Clean Truck Check (HD I/M) program?",
          a: "The CARB Clean Truck Check (formerly Heavy-Duty Inspection and Maintenance or HD I/M) is a mandatory California regulation for non-gasoline heavy-duty vehicles with a GVWR over 14,000 pounds. It requires periodic emissions testing to ensure emissions control systems are functioning properly and minimizing air pollution. Ramirez Mobile Clean Truck is fully certified to perform these official tests."
        },
        {
          q: "How often do I need a CARB Clean Truck Check in California for 2024?",
          a: "Starting in 2024, most commercial diesel vehicles operating in California are required to undergo compliance testing **twice per year (semi-annually)**. Agricultural vehicles and California-registered motorhomes currently require testing **once per year**. Tests must be submitted to CARB within 90 days before your vehicle's DMV compliance deadline."
        },
        {
          q: "What are the penalties for missing a CARB compliance deadline?",
          a: "Failing to comply with the California Air Resources Board Clean Truck Check program results in severe consequences, including DMV registration holds, significant financial penalties (often starting at hundreds of dollars per violation), and potential impoundment of non-compliant commercial trucks operating in California."
        }
      ]
    },
    {
      category: "Local Mobile Services (Inland Empire)",
      icon: FiMapPin,
      questions: [
        {
          q: "Do you offer mobile CARB testing in Ontario, CA and Fontana?",
          a: "Yes, Ramirez Mobile Clean Truck provides **100% mobile CARB compliance testing** directly at your truck yard, distribution center, or breakdown location in Ontario, Fontana, Rancho Cucamonga, Chino, and throughout the Inland Empire. We bring the testing equipment to you to eliminate your fleet's downtime."
        },
        {
          q: "How quickly can you arrive for a mobile diesel emissions test in San Bernardino County?",
          a: "Ramirez Mobile Clean Truck offers same-day and emergency dispatch services throughout San Bernardino County and Riverside County. If you have an urgent DMV registration hold or compliance deadline, call us at (909) 284-2738 for immediate mobile service."
        },
        {
          q: "Can you perform a DOT Annual Inspection at my truck yard in Rancho Cucamonga?",
          a: "Absolutely. In addition to CARB emissions testing, Ramirez Mobile Clean Truck performs complete mobile DOT Annual Inspections (FMCSA) and 90-Day BIT inspections at client facilities in Rancho Cucamonga, Upland, Pomona, and surrounding Southern California cities."
        }
      ]
    },
    {
      category: "Diagnostics & DPF Force Regens",
      icon: FiTruck,
      questions: [
        {
          q: "What is a DPF Force Regen and when do I need one?",
          a: "A forced regeneration (regen) is a manually initiated computer process that burns off accumulated soot and ash inside your Diesel Particulate Filter (DPF). You need a forced regen from Ramirez Mobile Clean Truck when your truck's automated system fails to clear the filter, resulting in an engine 'derate' (loss of power) or a persistent Check Engine light."
        },
        {
          q: "My diesel truck failed the opacity test. What happens next?",
          a: "If your truck fails the SAE J1667 smoke opacity test or has active emissions-related fault codes, it cannot receive a passing CARB certificate. Ramirez Mobile Clean Truck will immediately run comprehensive computer diagnostics to find the root cause (such as DPF, DEF, or SCR issues), provide repair recommendations, and can perform on-site force regens to clear soot loads and codes."
        }
      ]
    },
    {
      category: "Pricing & Automated Reminders",
      icon: FiMessageCircle,
      questions: [
        {
          q: "How much does a mobile Clean Truck Check cost in Southern California?",
          a: "Ramirez Mobile Clean Truck offers highly competitive local pricing:\n• **CARB Clean Truck Check:** Starts at $150 per truck.\n• **Smoke Opacity Testing (SAE J1667):** Starts at $250.\n• **Computer Diagnostics:** Starts at $120.\n*Note: We offer significant fleet discounts for 3 or more trucks tested at the same location in Ontario, Fontana, or nearby cities.*"
        },
        {
          q: "How do I sign up for automated CARB compliance reminders?",
          a: "Ramirez Mobile Clean Truck offers a **Free Automated Reminder Service** for fleet managers. By registering on our website, our system will automatically track your fleet and send you SMS text and Email alerts 30, 15, and 7 days before your CARB compliance or DOT inspection deadlines."
        }
      ]
    }
  ];

  // Glossary for AI Entity Extraction (Highly favored by Perplexity, Gemini & ChatGPT)
  const glossary = [
    { term: "CARB", definition: "California Air Resources Board. The state agency responsible for protecting public health from the harmful effects of air pollution, specifically regulating diesel fleet emissions." },
    { term: "HD I/M", definition: "Heavy-Duty Inspection and Maintenance. The official regulatory name for the California Clean Truck Check program." },
    { term: "DPF", definition: "Diesel Particulate Filter. An exhaust aftertreatment device that traps particulate matter (soot) from diesel engine exhaust." },
    { term: "DEF", definition: "Diesel Exhaust Fluid. A solution injected into the exhaust stream to reduce NOx emissions in SCR (Selective Catalytic Reduction) systems." },
    { term: "BIT Inspection", definition: "Biennial Inspection of Terminals. A California Highway Patrol (CHP) program requiring 90-day safety inspections for commercial fleets." },
    { term: "Opacity Test", definition: "SAE J1667 Snap-Acceleration test used to measure the amount of smoke (particulate matter) emitted from a heavy-duty diesel exhaust." }
  ];

  // Inject High-Quality JSON-LD FAQ Schema for Google Rich Snippets
  useEffect(() => {
    const scriptId = 'faq-schema-json-ld';
    let script = document.getElementById(scriptId);
    
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      
      // Flatten questions for Schema
      const allQuestions = faqData.flatMap(cat => cat.questions);
      
      const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": allQuestions.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a.replace(/\n/g, " ") // Clean formatting for strict JSON-LD compliance
          }
        }))
      };
      
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    }
    
    return () => {
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const toggleQuestion = (catIndex, qIndex) => {
    const id = `${catIndex}-${qIndex}`;
    setOpenQuestion(openQuestion === id ? null : id);
  };

  // Helper function to render text with bolding and line breaks
  const renderFormattedText = (text) => {
    return text.split('\n').map((line, i) => {
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <span key={i} className="block mb-2 last:mb-0">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j} className="text-white">{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </span>
      );
    });
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-blue-900 py-24 overflow-hidden border-b border-gray-800">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/a728ddab7d71aca898b8aa1e2ed2cb3f.jpeg" 
            alt="Customer support and FAQ for CARB Compliance" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/90 to-blue-900/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-block p-4 rounded-full bg-blue-500/20 mb-6 backdrop-blur-sm border border-blue-500/30"
            >
              <SafeIcon icon={FiMessageCircle} className="text-5xl text-blue-400" />
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Common Questions & Answers
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed"
            >
              Your comprehensive guide to CARB Clean Truck Checks, mobile diesel emissions testing, and commercial fleet compliance in Southern California.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Categorized FAQ Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Category Navigation Pills (Desktop) */}
            <div className="hidden md:flex justify-center flex-wrap gap-4 mb-12">
              {faqData.map((cat, idx) => (
                <button
                  key={idx}
                  onClick={() => setOpenCategory(idx)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all flex items-center gap-2 ${
                    openCategory === idx 
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white border border-gray-700'
                  }`}
                >
                  <SafeIcon icon={cat.icon} />
                  {cat.category}
                </button>
              ))}
            </div>

            {/* Render Active Category */}
            <div className="space-y-8">
              {/* Mobile Category Header */}
              <div className="md:hidden mb-6">
                <h2 className="text-2xl font-bold text-blue-400 flex items-center gap-2 border-b border-gray-800 pb-4">
                  <SafeIcon icon={faqData[openCategory].icon} />
                  {faqData[openCategory].category}
                </h2>
              </div>

              <div className="space-y-4">
                {faqData[openCategory].questions.map((faq, qIndex) => {
                  const id = `${openCategory}-${qIndex}`;
                  const isOpen = openQuestion === id;

                  return (
                    <motion.div 
                      key={qIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: qIndex * 0.05 }}
                      className={`bg-gray-800 border rounded-xl overflow-hidden transition-colors duration-300 ${
                        isOpen ? 'border-blue-500 shadow-lg shadow-blue-900/20' : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <button
                        onClick={() => toggleQuestion(openCategory, qIndex)}
                        className="w-full text-left px-6 py-5 flex items-start justify-between focus:outline-none group"
                        aria-expanded={isOpen}
                      >
                        <span className={`font-bold text-lg pr-8 transition-colors ${
                          isOpen ? 'text-blue-400' : 'text-gray-100 group-hover:text-blue-300'
                        }`}>
                          {faq.q}
                        </span>
                        <div className={`flex-shrink-0 mt-1 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                          isOpen ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 group-hover:bg-gray-600'
                        }`}>
                          <SafeIcon icon={isOpen ? FiMinus : FiPlus} />
                        </div>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          >
                            <div className="px-6 pb-6 text-gray-300 leading-relaxed border-t border-gray-700/50 pt-4 bg-gray-800/50">
                              {renderFormattedText(faq.a)}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Mobile Category Selector (Bottom) */}
            <div className="md:hidden mt-8 grid grid-cols-1 gap-3">
              <p className="text-sm text-gray-500 font-semibold mb-2">Other Categories:</p>
              {faqData.map((cat, idx) => (
                openCategory !== idx && (
                  <button
                    key={idx}
                    onClick={() => {
                      setOpenCategory(idx);
                      window.scrollTo({ top: 300, behavior: 'smooth' });
                    }}
                    className="w-full text-left px-4 py-3 rounded-lg bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 flex items-center gap-3"
                  >
                    <SafeIcon icon={cat.icon} />
                    {cat.category}
                  </button>
                )
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Glossary / Knowledge Base Section (Crucial for AI Search Optimization) */}
      <section className="py-20 bg-gray-800 border-t border-gray-700">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center p-3 bg-gray-700 rounded-full mb-4">
                <SafeIcon icon={FiBookOpen} className="text-2xl text-blue-400" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">Fleet Compliance Glossary</h2>
              <p className="text-gray-400">Key industry terms and acronyms AI search engines and fleet managers use to stay compliant in California.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {glossary.map((item, idx) => (
                <div key={idx} className="bg-gray-900 border border-gray-700 p-6 rounded-xl hover:border-gray-600 transition-colors">
                  <h3 className="text-xl font-bold text-blue-400 mb-2 border-b border-gray-800 pb-2 inline-block">
                    {item.term}
                  </h3>
                  <p className="text-gray-300 text-sm leading-relaxed mt-2">
                    {item.definition}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Trust Banner */}
      <section className="py-12 bg-blue-900 border-t border-b border-blue-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                <SafeIcon icon={FiShield} className="text-3xl text-white" />
              </div>
              <div>
                <h4 className="font-bold text-white text-lg">CARB Certified</h4>
                <p className="text-sm text-blue-200 mt-1">Official HD I/M testing credentials</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="text-3xl text-white" />
              </div>
              <div>
                <h4 className="font-bold text-white text-lg">Guaranteed Accuracy</h4>
                <p className="text-sm text-blue-200 mt-1">State-of-the-art diagnostic equipment</p>
              </div>
            </div>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
                <SafeIcon icon={FiTruck} className="text-3xl text-white" />
              </div>
              <div>
                <h4 className="font-bold text-white text-lg">100% Mobile Service</h4>
                <p className="text-sm text-blue-200 mt-1">We come directly to your fleet yard</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-20 bg-gray-900 text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4">Still have questions?</h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Our compliance experts are ready to help you navigate California's emissions regulations and get your fleet certified in Ontario and the Inland Empire today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:9092842738" 
              className="bg-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg flex items-center justify-center space-x-2 shadow-lg"
            >
              <SafeIcon icon={FiPhone} />
              <span>Call (909) 284-2738</span>
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-gray-600 text-white px-8 py-4 rounded-lg hover:bg-white hover:text-gray-900 hover:border-white transition-all text-lg"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;